import { supabase } from "@/infrastructure/supabase/client";
import type { DecisionRow, Status } from "@/infrastructure/supabase/database.types";

export interface DecisionFilters {
  status?: Status;
  phaseId?: string;
  search?: string;
}

export const decisionsRepository = {
  async list(filters: DecisionFilters = {}): Promise<DecisionRow[]> {
    let q = supabase.from("decisions").select("*").order("created_at", { ascending: false });
    if (filters.status) q = q.eq("status", filters.status);
    if (filters.phaseId) q = q.eq("phase_id", filters.phaseId);
    if (filters.search) q = q.ilike("title", `%${filters.search}%`);
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  },

  async byId(id: string): Promise<DecisionRow | null> {
    const { data, error } = await supabase.from("decisions").select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    return data;
  },

  async create(input: Omit<DecisionRow, "id" | "created_at" | "updated_at">): Promise<DecisionRow> {
    const { data, error } = await supabase.from("decisions").insert(input).select("*").single();
    if (error) throw error;
    return data;
  },

  async update(id: string, patch: Partial<DecisionRow>): Promise<DecisionRow> {
    // Auto-set decided_at when status flips to resolved.
    const next: Partial<DecisionRow> = { ...patch };
    if (patch.status === "resolved" && !patch.decided_at) {
      next.decided_at = new Date().toISOString();
    }
    const { data, error } = await supabase
      .from("decisions")
      .update(next)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("decisions").delete().eq("id", id);
    if (error) throw error;
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("decisions-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "decisions" }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
